import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, MessageCircle } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { generateTelegramLink } from "../data/telegramLinks";
import { products } from "../data/products";

const COACH_IMG = "https://customer-assets.emergentagent.com/job_7ec29116-ad53-40a7-8679-337a3668cf90/artifacts/emec1ab0_photo_2026-01-20%2014.12.47.jpeg";

// Reduced from 4 to 2 floating cards for less visual noise
const floatingCards = [
  { id: "nexus-vision", top: "8%", right: "-8%", delay: 0.2 },
  { id: "strategyx-lab", bottom: "12%", left: "-12%", delay: 0.4 },
];

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const { t } = useLanguage();
  const trust = [t("trust1"), t("trust2"), t("trust3"), t("trust4"), t("trust5")];

  return (
    <section
      id="top"
      className="relative pt-36 md:pt-44 pb-24 md:pb-32 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 bg-grid opacity-50 hidden md:block" aria-hidden />
      <div className="absolute inset-0 spotlight" aria-hidden />
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#D7B56D]/[0.04] blur-3xl" aria-hidden />

      <div className="relative max-w-[1440px] mx-auto px-5 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left side */}
          <div className="lg:col-span-7 z-10">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="inline-flex items-center gap-2.5 mb-8 px-4 py-2 rounded-full glass border border-[#D7B56D]/24"
              data-testid="hero-eyebrow"
            >
              <span className="status-dot w-1.5 h-1.5 rounded-full bg-[#76D9E8]" />
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#D7B56D]">
                {t("heroEyebrow")}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, delay: 0.1, ease: EASE }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] font-black leading-[0.95] tracking-tighter text-[#F5F1EA]"
              data-testid="hero-title"
            >
              {t("heroTitle").split(" ").map((word, i, arr) => {
                const isAccent = word.toLowerCase().includes("learning") || word.toLowerCase().includes("обучен") || word.toLowerCase().includes("apprentissage") || word.toLowerCase().includes("aprendiz") || word.toLowerCase().includes("öğren") || word.toLowerCase().includes("học") || word.toLowerCase().includes("لرننگ") || word.toLowerCase().includes("التعلم");
                return (
                  <React.Fragment key={i}>
                    {isAccent ? (
                      <span className="text-gold-shine italic font-medium">{word}</span>
                    ) : (
                      word
                    )}
                    {i < arr.length - 1 ? " " : ""}
                  </React.Fragment>
                );
              })}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.25, ease: EASE }}
              className="mt-7 max-w-2xl text-base md:text-[17px] text-[#E6E0D6]/90 leading-relaxed"
              data-testid="hero-sub"
            >
              {t("heroSub")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.4, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-3"
            >
              <a href="#accesses" className="btn-gold" data-testid="hero-cta-1">
                {t("heroCta1")} <ArrowRight size={16} />
              </a>
              <a href="#help" className="btn-ghost" data-testid="hero-cta-2">
                <Sparkles size={14} className="text-[#D7B56D]" /> {t("heroCta2")}
              </a>
              <a
                href={generateTelegramLink("private-support")}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost"
                data-testid="hero-cta-3"
              >
                <MessageCircle size={14} className="text-[#76D9E8]" /> {t("heroCta3")}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
              className="mt-9 flex flex-wrap items-center gap-x-5 gap-y-2"
              data-testid="hero-trust-strip"
            >
              {trust.map((item, i) => (
                <span key={i} className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.16em] text-[#A9824A]">
                  <span className="w-1 h-1 rounded-full bg-[#D7B56D]" />
                  {item}
                </span>
              ))}
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
              className="mt-8 text-[12px] text-[#D8DCE2]/55 max-w-xl leading-relaxed"
              data-testid="hero-disclaimer"
            >
              {t("disclaimer")}
            </motion.p>
          </div>

          {/* Right side: Coach card with reduced floating mini-cards */}
          <div className="lg:col-span-5 relative h-[440px] sm:h-[540px] lg:h-[640px]">
            {floatingCards.map((fc, i) => {
              const product = products.find((p) => p.id === fc.id);
              if (!product) return null;
              return (
                <motion.div
                  key={fc.id}
                  initial={{ opacity: 0, y: 12, scale: 0.94 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.9, delay: 0.6 + fc.delay, ease: EASE }}
                  className="absolute hidden md:flex animate-float-slow"
                  style={{
                    top: fc.top,
                    bottom: fc.bottom,
                    left: fc.left,
                    right: fc.right,
                    animationDelay: `${i * 0.6}s`,
                    zIndex: 5,
                  }}
                >
                  <div className="glass rounded-2xl p-3.5 pr-5 flex items-center gap-3 min-w-[210px] border border-[#D7B56D]/16">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center font-mono text-[10px] tracking-tight"
                      style={{
                        background: `linear-gradient(135deg, ${product.accent === "gold" ? "#D7B56D" : product.accent === "cyan" ? "#76D9E8" : "#A9824A"}, transparent)`,
                        color: "#0a0b0f",
                      }}
                    >
                      {product.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                    </div>
                    <div>
                      <div className="font-display text-[12px] font-bold text-[#F5F1EA] leading-none">
                        {product.name}
                      </div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#A9824A] mt-1">
                        {product.level[0]}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* Main coach card */}
            <motion.div
              initial={{ opacity: 0, y: 18, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1.05, delay: 0.45, ease: EASE }}
              className="absolute inset-x-4 sm:inset-x-12 lg:inset-x-0 top-0 bottom-0 z-10"
              data-testid="hero-coach-card"
            >
              <div className="relative h-full w-full rounded-3xl overflow-hidden border border-[#D7B56D]/26 glass-strong">
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url("${COACH_IMG}")` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#07090D] via-[#07090D]/55 to-transparent" />

                <div className="absolute top-5 left-5 right-5 flex justify-between items-start">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full glass border border-[#76D9E8]/35">
                    <span className="status-dot w-1.5 h-1.5 rounded-full bg-[#76D9E8]" />
                    <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#76D9E8]">
                      Online
                    </span>
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#D8DCE2]/60">
                    Coach · 01
                  </div>
                </div>

                <div className="absolute bottom-0 inset-x-0 p-6 md:p-8">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#D7B56D] mb-2.5">
                    Learning Coach
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-bold text-[#F5F1EA] leading-tight mb-2.5">
                    {t("coachTitle")}
                  </h3>
                  <p className="text-[13px] text-[#E6E0D6]/85 leading-relaxed mb-6 max-w-md">
                    {t("coachText")}
                  </p>
                  <a
                    href={generateTelegramLink("private-support")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F5F1EA] text-[#0a0b0f] text-[12px] font-bold tracking-tight hover:bg-[#D7B56D] transition-colors duration-200"
                    data-testid="hero-coach-cta"
                  >
                    <MessageCircle size={13} /> {t("ctaAskTelegram")}
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
